import React, { useMemo, useRef, useState } from 'react';
import { drawerSections } from './content';
import { createTreatyMutation, getReinsurersListQuery } from './queries';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { useNavigate } from 'react-router-dom';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DateService from '@services/DateService';
import FormattingService from '@services/FormattingService';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IAbstractRecord, IPagination } from '@models';
import { useAppSelector } from '@redux/hooks';

interface INewTreatyDrawer {
  isOpen: boolean;
  onClose: () => void;
  lovs: Record<string, Record<string, string>>;
}

export interface IReinsurer {
  reinsurer: string;
  share: number;
}

interface ITreatyDrawerValues {
  company: string;
  treatyName: string;
  treatyType: string;
  currency: string;
  effectiveDate: string;
  expiryDate: string;
  underwritingLimit: number;
  claimadviseLimit: number;
  substandardLimit: string;
  reinsurersRepeater: IReinsurer[];
}

const NewTreatyDrawer: React.FC<INewTreatyDrawer> = ({
  isOpen,
  onClose,
  lovs,
}) => {
  const navigate = useNavigate();
  const { currency } = useAppSelector((state) => state.lovs);
  const currencySymbols = useMemo(() => {
    return lookupListAsRecordObject(currency, false, 'Code', 'Symbol');
  }, [currency]);
  const [values, setValues] = useState<ITreatyDrawerValues>({
    company: '41',
    treatyName: null,
    treatyType: null,
    currency: '109',
    effectiveDate: null,
    expiryDate: null,
    underwritingLimit: null,
    claimadviseLimit: null,
    substandardLimit: null,
    reinsurersRepeater: [
      {
        reinsurer: null,
        share: null,
      },
    ],
  });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const [getReinsurersLazy] = useLazyQuery(getReinsurersListQuery);
  const [createTreaty, { loading }] = useMutation(createTreatyMutation);

  const handleChange = (
    fieldName: string,
    value: unknown,
    sectionIndex: number,
    allValues: ITreatyDrawerValues
  ) => {
    if (
      fieldName === 'treatyType' &&
      (value as string).toLowerCase() === 'proportional'
    ) {
      allValues.substandardLimit = '100';
    }

    setValues(allValues);
    validateForm(allValues);
  };

  // this is used to keep track of the selected reinsurers names
  const reinsurersList = useRef<Record<string, string>>({});
  const updateReinsurersList = (options: IAbstractRecord) => {
    // merge the new options with the existing ones and keep them unique
    reinsurersList.current = {
      ...reinsurersList.current,
      ...options,
    };
  };

  const handleSubmit = async () => {
    try {
      const res = await createTreaty({
        variables: {
          Company: values.company,
          TreatyName: values.treatyName,
          SelectedType: FormattingService.formatEnumToBackend(
            values.treatyType
          ),
          SelectedCurrency: values.currency,
          UnderwritingLimit: Number(values.underwritingLimit),
          ClaimLimit: Number(values.claimadviseLimit),
          SubstandardLimit: Number(values.substandardLimit),
          EffecitiveDate: DateService.formatDateBackend(values.effectiveDate),
          ExpiryDate: DateService.formatDateBackend(values.expiryDate),
          ReinsurersList: values.reinsurersRepeater
            .map((item, index) => ({
              IsLeader: index === 0,
              ReinsurerID: item.reinsurer,
              ReinsurerName: reinsurersList.current[item.reinsurer],
              ReinsurerShare: Number(item.share),
            }))
            .sort((a) => (a.IsLeader ? 1 : -1)),
        },
      });

      const createdTreatyId = res.data?.production?.actions?.createTreaty?.id;

      if (createdTreatyId) {
        toast.success(
          <ToastSuccessMessage>Treaty created successfully</ToastSuccessMessage>
        );
        setTimeout(() => {
          navigate('/reinsurance/treaties/' + createdTreatyId);
        }, 750);
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
    }
  };

  const validateForm = (values: ITreatyDrawerValues) => {
    const Invalid =
      !values.company ||
      !values.treatyName ||
      !values.treatyType ||
      !values.currency ||
      !values.effectiveDate ||
      !values.expiryDate ||
      !values.underwritingLimit ||
      !values.claimadviseLimit ||
      (values.treatyType?.toLowerCase() === 'proportional' &&
        !values.substandardLimit) ||
      values.reinsurersRepeater.some((item) => !item.reinsurer || !item.share);

    setIsSubmitDisabled(Invalid);
  };

  const handleSearch = (
    fieldName: string,
    value: string,
    pagination: IPagination
  ) => {
    return getReinsurersLazy({
      variables: {
        SelectedCompany: '41',
        KeywordSearch: value,
        pagination: pagination,
      },
    }).then((result) => {
      const options = lookupListAsRecordObject(
        result.data?.Production?.queries?.ReinsurersList?.items,
        false,
        'salesforceManagement_BusinessPartner_Id',
        'salesforceManagement_BusinessPartner_FullName'
      );
      updateReinsurersList(options); // update the list of reinsurers for external use
      return {
        options,
        totalCount:
          result.data?.Production?.queries?.ReinsurersList?.paging?.totalCount,
      };
    });
  };

  return (
    <DynamicFormDrawer
      title={'Add New Treaty'}
      onClose={() => onClose()}
      isOpen={isOpen}
      disableForm={loading}
      isSubmitButtonDisabled={isSubmitDisabled || loading}
      onSubmit={handleSubmit}
      sections={drawerSections(values || {}, {
        ...lovs,
        currencySymbols: currencySymbols,
      })}
      onChange={handleChange}
      onAutocompleteSearch={handleSearch}
    />
  );
};

export default NewTreatyDrawer;
